import { useState, useEffect } from "react";
import { useToast } from "./Toast";
import { apiUrl } from "../config/api";

interface Team {
  id: string;
  name: string;
  ownerEmail: string;
  members: Array<{
    email: string;
    role: "owner" | "admin" | "member" | "viewer";
    joinedAt: string;
    invitedBy: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface TeamInvite {
  id: string;
  teamId: string;
  email: string;
  role: "admin" | "member" | "viewer";
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  accepted: boolean;
}

export function TeamManager() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [teamScans, setTeamScans] = useState<any[]>([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin" | "viewer">("member");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiUrl("/teams"), { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTeams(data.teams || []);
        setInvites(data.invites || []);
      }
    } catch (err) {
      setError("Failed to load teams");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName) {
      toast('Team name is required', 'warning');
      return;
    }

    try {
      const res = await fetch(apiUrl("/teams"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newTeamName })
      });

      if (res.ok) {
        const newTeam = await res.json();
        setTeams([...teams, newTeam]);
        setNewTeamName("");
        setShowCreateForm(false);
      } else {
        const status = res.status;
        toast(status === 403 ? 'Permission denied: unable to create team' : `Failed to create team (error ${status})`, 'error');
      }
    } catch (err) {
      setError("Failed to create team");
      console.error(err);
    }
  };

  const selectTeam = async (team: Team) => {
    setSelectedTeam(team);
    try {
      const res = await fetch(apiUrl(`/teams/${team.id}/scans`), { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTeamScans(data.scans || []);
      }
    } catch (err) {
      console.error("Failed to load team scans:", err);
    }
  };

  const inviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam || !inviteEmail) return;

    try {
      const res = await fetch(apiUrl(`/teams/${selectedTeam.id}/invite`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: inviteEmail, role: inviteRole })
      });

      if (res.ok) {
        setInviteEmail("");
        setShowInviteForm(false);
        fetchTeams();
        selectTeam(selectedTeam);
      } else {
        setError("Failed to invite member");
      }
    } catch (err) {
      setError("Failed to invite member");
      console.error(err);
    }
  };

  const acceptInvite = async (inviteId: string, teamId: string) => {
    try {
      const res = await fetch(apiUrl(`/teams/${teamId}/accept`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ inviteId })
      });

      if (res.ok) {
        fetchTeams();
      }
    } catch (err) {
      setError("Failed to accept invite");
      console.error(err);
    }
  };

  const removeMember = async (email: string) => {
    if (!selectedTeam) return;

    try {
      const res = await fetch(apiUrl(`/teams/${selectedTeam.id}/members/${encodeURIComponent(email)}`), {
        method: "DELETE",
        credentials: "include"
      });

      if (res.ok) {
        fetchTeams();
        selectTeam(selectedTeam);
      }
    } catch (err) {
      setError("Failed to remove member");
      console.error(err);
    }
  };

  const containerStyle: React.CSSProperties = {
    display: "flex",
    height: "100%",
    gap: "20px",
    padding: "20px",
    backgroundColor: "var(--bg-primary)",
    color: "var(--text-primary)"
  };

  const sidebarStyle: React.CSSProperties = {
    flex: "0 0 300px",
    borderRight: "1px solid var(--border-color)",
    overflowY: "auto",
    paddingRight: "20px"
  };

  const mainStyle: React.CSSProperties = {
    flex: "1",
    overflowY: "auto"
  };

  const teamItemStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: "10px 15px",
    marginBottom: "8px",
    backgroundColor: isSelected ? "var(--nav-active-bg)" : "var(--nav-inactive-bg)",
    color: isSelected ? "var(--nav-active-text)" : "var(--nav-inactive-text)",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s"
  });

  const buttonStyle: React.CSSProperties = {
    padding: "10px 15px",
    backgroundColor: "var(--primary-color)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px"
  };

  const formStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginTop: "15px",
    padding: "15px",
    backgroundColor: "var(--nav-inactive-bg)",
    borderRadius: "6px"
  };

  const inputStyle: React.CSSProperties = {
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid var(--border-color)",
    backgroundColor: "var(--bg-primary)",
    color: "var(--text-primary)"
  };

  if (loading) {
    return <div style={containerStyle}>Loading teams...</div>;
  }

  return (
    <div style={containerStyle}>
      <div style={sidebarStyle}>
        <h2 style={{ fontSize: "18px", marginBottom: "15px" }}>My Teams</h2>

        {invites.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <h3 style={{ fontSize: "14px", marginBottom: "10px", color: "var(--secondary-color)" }}>Pending Invites</h3>
            {invites.map(invite => (
              <div
                key={invite.id}
                style={{
                  padding: "10px",
                  backgroundColor: "var(--nav-inactive-bg)",
                  marginBottom: "8px",
                  borderRadius: "6px",
                  fontSize: "13px"
                }}
              >
                <p style={{ margin: "0 0 8px 0" }}>Invited to {invite.teamId}</p>
                <button
                  onClick={() => acceptInvite(invite.id, invite.teamId)}
                  style={{
                    ...buttonStyle,
                    padding: "6px 12px",
                    fontSize: "12px",
                    backgroundColor: "var(--secondary-color)"
                  }}
                >
                  Accept
                </button>
              </div>
            ))}
          </div>
        )}

        {teams.map(team => (
          <div
            key={team.id}
            style={teamItemStyle(selectedTeam?.id === team.id)}
            onClick={() => selectTeam(team)}
          >
            <div style={{ fontWeight: "500" }}>{team.name}</div>
            <div style={{ fontSize: "12px", opacity: 0.7 }}>{team.members.length} members</div>
          </div>
        ))}

        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{ ...buttonStyle, marginTop: "15px", width: "100%" }}
        >
          Create Team
        </button>

        {showCreateForm && (
          <form onSubmit={createTeam} style={formStyle}>
            <input
              type="text"
              placeholder="Team name"
              value={newTeamName}
              onChange={e => setNewTeamName(e.target.value)}
              style={inputStyle}
              maxLength={100}
            />
            <button type="submit" style={buttonStyle}>
              Create
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              style={{ ...buttonStyle, backgroundColor: "var(--border-color)" }}
            >
              Cancel
            </button>
          </form>
        )}

        {error && (
          <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#ff4444", borderRadius: "6px" }}>
            {error}
          </div>
        )}
      </div>

      <div style={mainStyle}>
        {selectedTeam ? (
          <>
            <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>{selectedTeam.name}</h2>

            <section style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "16px", marginBottom: "15px" }}>Members</h3>
              <div style={{ display: "grid", gap: "10px" }}>
                {selectedTeam.members.map(member => (
                  <div
                    key={member.email}
                    style={{
                      padding: "12px",
                      backgroundColor: "var(--nav-inactive-bg)",
                      borderRadius: "6px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "500" }}>{member.email}</div>
                      <div style={{ fontSize: "12px", opacity: 0.7 }}>Role: {member.role}</div>
                    </div>
                    {member.role !== "owner" && (
                      <button
                        onClick={() => removeMember(member.email)}
                        style={{
                          ...buttonStyle,
                          backgroundColor: "var(--error-color)",
                          padding: "6px 12px"
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowInviteForm(!showInviteForm)}
                style={{ ...buttonStyle, marginTop: "15px" }}
              >
                Invite Member
              </button>

              {showInviteForm && (
                <form onSubmit={inviteMember} style={formStyle}>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    style={inputStyle}
                  />
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value as any)}
                    style={inputStyle}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button type="submit" style={buttonStyle}>
                    Send Invite
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInviteForm(false)}
                    style={{ ...buttonStyle, backgroundColor: "var(--border-color)" }}
                  >
                    Cancel
                  </button>
                </form>
              )}
            </section>

            {teamScans.length > 0 && (
              <section>
                <h3 style={{ fontSize: "16px", marginBottom: "15px" }}>Team Scans ({teamScans.length})</h3>
                <div style={{ display: "grid", gap: "10px" }}>
                  {teamScans.map(scan => (
                    <div
                      key={scan.scanId}
                      style={{
                        padding: "12px",
                        backgroundColor: "var(--nav-inactive-bg)",
                        borderRadius: "6px"
                      }}
                    >
                      <div style={{ fontWeight: "500" }}>{scan.metadata?.filename || scan.scanId}</div>
                      <div style={{ fontSize: "12px", opacity: 0.7 }}>By {scan.ownerEmail}</div>
                      <div style={{ fontSize: "12px", opacity: 0.7 }}>
                        {new Date(scan.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        ) : (
          <div style={{ fontSize: "16px", color: "var(--text-secondary)" }}>
            Select a team to view details
          </div>
        )}
      </div>
    </div>
  );
}
